import {
  useMfaSmsChallenge,
  useScreen,
  useUser,
} from '@auth0/auth0-acul-react/mfa-sms-challenge';

import { MFA_REDIRECT_COOKIE } from '@/constants/mfa';
import { setCookie } from '@/utils/helpers/cookie';

export const useMfaSmsChallengeManager = () => {
  const mfaSmsChallenge = useMfaSmsChallenge();
  const screen = useScreen();
  const user = useUser();

  const { texts, data, links } = screen ?? {};

  // Extract phone number from screen data
  const phoneNumber = data?.phoneNumber;
  const showRememberDevice = data?.showRememberDevice ?? false;

  // Check available actions
  const canEditIdentifier = !!links?.edit_identifier;
  const canSelectFactor =
    typeof mfaSmsChallenge.tryAnotherMethod === 'function';
  const canPickSms = !!links?.pickSms;

  const handleContinueMfaSmsChallenge = async (
    code: string,
    rememberDevice: boolean = false,
  ): Promise<void> => {
    await mfaSmsChallenge.continueMfaSmsChallenge({
      code: code?.trim() || '',
      rememberDevice,
    });
  };

  const handleResendCode = async (): Promise<void> => {
    await mfaSmsChallenge.resendCode({});
  };

  // Edit identifier (modify phone number) - navigate to the link
  const handleEditIdentifier = (): void => {
    if (links?.edit_identifier) {
      window.location.assign(links.edit_identifier);
    }
  };

  // Pick SMS (select from list of phone numbers)
  const handlePickSms = async (): Promise<void> => {
    await mfaSmsChallenge.pickSms({});
  };

  // Try another method (select a different authenticator)
  // Sets a cookie to auto-redirect to email on mfa-login-options
  const handleTryAnotherMethod = async (): Promise<void> => {
    setCookie(MFA_REDIRECT_COOKIE, 'email');
    await mfaSmsChallenge.tryAnotherMethod({});
  };

  // If user already has enrolled phone numbers, this is a login challenge
  // Otherwise it's an enrollment verification (post-setup)
  const isLoginChallenge = (user?.enrolledPhoneNumbers?.length ?? 0) > 0;

  return {
    mfaSmsChallenge,
    isLoginChallenge,
    handleContinueMfaSmsChallenge,
    handleResendCode,
    handleTryAnotherMethod,
    handlePickSms,
    handleEditIdentifier,
    phoneNumber,
    canEditIdentifier,
    canSelectFactor,
    canPickSms,
    showRememberDevice,
    texts: texts ?? {},
    data,
    links,
  };
};
